import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiError, TransportError, isSessionExpired } from "./errors";

const MAX_RETRIES = 2;

/**
 * Retries only what a second try can fix: a lost connection or a 5xx. A 4xx is the server's
 * answer about this request, and a malformed body will be malformed again.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (error instanceof TransportError) {
    return error.kind === "network" || (error.kind === "http" && (error.status ?? 0) >= 500);
  }
  return error instanceof ApiError && error.status >= 500;
}

export interface QueryClientOptions {
  /** Called on every UNAUTHENTICATED (401) error of a query or a mutation: the one path for an
   * ended session. */
  onSessionExpired?: () => void;
}

/**
 * The one QueryClient factory. Server state lives in this cache and nowhere else (RC1): no store
 * copies a response, and a change on the server reaches the UI by invalidating a query here.
 * Mutations keep TanStack's default of no retries, so a POST is never sent twice by the client.
 */
export function createQueryClient({ onSessionExpired }: QueryClientOptions = {}): QueryClient {
  const onError = (error: unknown): void => {
    if (isSessionExpired(error)) onSessionExpired?.();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      // No re-read on window focus (#794: each focus re-read every query of a page, 46 calls in
      // 4.5 min on the library). A read is fresh again after our own write (invalidation) or the
      // event stream; a query that must follow focus says so itself (a job that can still change,
      // signed URLs that are due).
      queries: { staleTime: 10_000, retry: shouldRetry, refetchOnWindowFocus: false },
    },
  });
}
